<template>
  <FileDifMenu
    :project-param-id="projectParamId"
    :xsl-param-id="currentRequestId"
    :xml-param-id="xmlParamId"
    :diff-view="getType"
  />

  <div class="diff-container">
    <!-- Sidebar -->
    <div v-show="isSidebarVisible" class="sidebar" id="leftBar">
      <div
        class="sidebar-header d-flex align-items-center justify-content-between"
      >
        <h5 class="fw-bold mb-0">XSLT Files</h5>
        <button class="btn btn-sm btn-light toggle-btn" @click="toggleSidebar">
          <i
            :class="
              isSidebarVisible ? 'fas fa-chevron-left' : 'fas fa-chevron-right'
            "
          ></i>
        </button>
      </div>

      <ul class="list-group mt-3 file-list">
        <li
          v-for="file in files"
          :key="file.xslFileName"
          class="list-group-item text-truncate file-item"
          :class="{ active: file.xslFileName === selectedFile.xslFileName }"
          @click="selectFile(file)"
        >
          {{ file.xslFileName }}
        </li>
      </ul>
    </div>

    <!-- Right Content Area -->
    <div class="content-area" id="rightBar">
      <div v-if="loading" class="process-loading">
        <div class="lds-roller">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>

      <div v-else class="diff-viewer">
        <button
          v-show="!isSidebarVisible"
          class="btn btn-sm btn-light toggle-btn float-start mt-2 ms-2"
          @click="toggleSidebar"
        >
          <i
            :class="
              isSidebarVisible ? 'fas fa-chevron-left' : 'fas fa-chevron-right'
            "
          ></i>
        </button>

        <div class="code-diff-wrapper">
          <CodeDiff
              ref="diffScrollWrapper"
              :old-string="leftFile"
              :new-string="rightFile"
              output-format="side-by-side"
              style="height: 75vh; padding: 0px; overflow: auto"
              language="XML"
              :filename="leftFileName"
              :newFilename="rightFileName"
              class="col"
            />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FileDifMenu from "../XmlandFileDifMenu.vue";
import { getCookie } from "../../store/cookie.js";
import { getSessionItem, setSessionItem } from "../../store/session.js";
import { type } from "@/utils/util";
import { makeOpearationRequest } from "@/services/apiServices";
import Split from "split.js";
import { pageViews } from "@/utils/util";

export default {
  name: pageViews.XSL_FILE_DIFF,
  setup() {
    return {
      type,
      makeOpearationRequest,
    };
  },
  data() {
    return {
      currentRequestId: 0,
      files: [],
      selectedFile: {},
      loading: true,
      leftFile: "",
      rightFile: "",
      leftFileName: "",
      rightFileName: "",
      isSidebarVisible: true,
      offset: 0,
      limit: 1000,
      hasMore: true,
      diffChunks: [],
      lastScrollTop: 0, // Track last scroll position
    };
  },

  components: {
    FileDifMenu,
  },
  mounted() {
    this.files = this.requestIds;
    const sessionSelectedFile = getSessionItem("xslDiffLastSelectedFile");
    this.selectedFile = sessionSelectedFile
      ? sessionSelectedFile
      : this.files[0];
    this.currentRequestId = this.files[0].xslId;
    setSessionItem("currentXslId", this.currentRequestId);
    this.resetAndLoadDiff();
    // Split.js expects #leftBar and #rightBar to be direct DOM elements, not components
    Split(["#leftBar", "#rightBar"], {
      sizes: [20, 80],
      minSize: [200, 400],
      gutterSize: 3,
      cursor: "col-resize",
    });
    this.$nextTick(() => {
      // Use $refs.diffScrollWrapper.$el if CodeDiff is a Vue component
      const diffScroller =
        this.$refs.diffScrollWrapper?.$el || this.$refs.diffScrollWrapper;
      if (diffScroller) {
        diffScroller.addEventListener("scroll", this.handleDiffScroll);
      }
    });
  },
  beforeUnmount() {
    const diffScroller =
      this.$refs.diffScrollWrapper?.$el || this.$refs.diffScrollWrapper;
    if (diffScroller) {
      diffScroller.removeEventListener("scroll", this.handleDiffScroll);
    }
  },
  computed: {
    getType() {
      return type.XSL;
    },
    userId() {
      return getCookie("userId");
    },
    requestIds() {
      return getSessionItem("xslRequestIds");
    },
  },
  methods: {
    toggleSidebar() {
      this.isSidebarVisible = !this.isSidebarVisible;
    },
    selectFile(file) {
      setSessionItem("xslDiffLastSelectedFile", file);
      this.selectedFile = file;
      this.currentRequestId = file.xslId;
      setSessionItem("currentXslId", this.currentRequestId);
      this.resetAndLoadDiff();
    },
    resetAndLoadDiff() {
      this.offset = 0;
      this.hasMore = true;
      this.diffChunks = [];
      this.leftFile = "";
      this.rightFile = "";
      this.leftFileName = "";
      this.rightFileName = "";
      this.loading = true;
      this.loadMoreDiff();
    },
    // Remove old handleScroll, add new handleDiffScroll for the diff area
    handleDiffScroll() {
      // Use $refs.diffScrollWrapper.$el if CodeDiff is a Vue component
      const diffScroller =
        this.$refs.diffScrollWrapper?.$el || this.$refs.diffScrollWrapper;
      if (!diffScroller || this.loading || !this.hasMore) return;
      const threshold = 2; // px from the bottom
      const isScrollingDown = diffScroller.scrollTop > this.lastScrollTop;
      const atEnd =
        diffScroller.scrollTop + diffScroller.clientHeight >=
        diffScroller.scrollHeight - threshold;
      // Only trigger if scrolling down and at end
      if (isScrollingDown && atEnd) {
        this.loadMoreDiff();
      }
      this.lastScrollTop = diffScroller.scrollTop;
    },
    async loadMoreDiff() {
      this.loading = true;
      const data = await this.showDifference(this.offset, this.limit);
      if (data) {
        if (this.offset === 0) {
          this.leftFileName = "Source File";
          let rightFileTitle = "Converted File";
          if ("tiboCount" in data && data.tiboCount == 0) {
            rightFileTitle =
              "Converted File - No TIBCO functions in source file";
          }
          this.rightFileName = rightFileTitle;
        }
        // Split by lines for chunking
        const leftLines = (data.inputXsl || "").split("\n");
        const rightLines = (data.convertedXsl || "").split("\n");
        // Get the chunk for the current offset
        const leftChunk = leftLines.slice(0, this.limit);
        const rightChunk = rightLines.slice(0, this.limit);

        if (this.offset === 0) {
          // First load, set content
          this.leftFile = leftChunk.join("\n");
          this.rightFile = rightChunk.join("\n");
        } else {
          // Append new chunk to existing content
          this.leftFile += "\n" + leftChunk.join("\n");
          this.rightFile += "\n" + rightChunk.join("\n");
        }

        // Increase offset for next chunk
        this.offset += this.limit;

        // If there are more lines to fetch, update hasMore
        const moreLeft = leftLines.length >= this.limit;
        const moreRight = rightLines.length >= this.limit;
        this.hasMore = moreLeft || moreRight;
      } else {
        this.hasMore = false;
      }
      this.loading = false;
    },
    async showDifference(offset = 0, limit = 1000) {
      var body = {
        userId: this.userId,
        type: type.XSL,
        requestId: this.currentRequestId,
        offset,
        limit,
        chunked: true, // <-- Add this line to inform backend to send chunked data
      };
      const endPoint = "/compare";
      try {
        const response = await this.makeOpearationRequest(
          this.$acclConfigs,
          endPoint,
          body
        );
        console.log("Response", response);
        if (response.status === 200) {
          return response.data;
        } else {
          console.log("Failure in File Comparision :", response.data.message);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  props: ["projectParamId"],
};
</script>
<style scoped>
.diff-container {
  display: flex;
  height: calc(100vh - 150px);
  border: 1px solid var(--border-color);
  overflow: hidden;
  background-color: var(--surface);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

/* Sidebar */
.sidebar {
  width: 250px;
  background-color: var(--surface-hover);
  border-right: 1px solid var(--border-color);
  color: var(--text-primary);
  display: flex;
  flex-direction: column;
  padding: 1rem 0.75rem;
  transition: all 0.3s ease;
}

.sidebar-header {
  position: sticky;
  top: 0;
  background: var(--surface-hover);
  z-index: 2;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

.file-list {
  direction: rtl; /* forces scrollbar to left */
  text-align: left; /* prevents text flipping alignment */

  overflow-y: auto;
  max-height: calc(75vh - 50px);
  
  padding: 6px 4px;       /* adds space inside the scroll area */
  margin-top: 12px;
  
  display: flex;
  flex-direction: column;
  gap: 2px;               /* space between each item */
  
}

/* Chrome scrollbar */
.file-list::-webkit-scrollbar {
  width: 2px;
}
.file-list::-webkit-scrollbar-track {
  background: var(--surface);
}
.file-list::-webkit-scrollbar-thumb {
  background: var(--primary);
}
.file-list::-webkit-scrollbar-thumb:hover {
  background: var(--primary-hover);
}

/* File items */
.file-item {
  direction: ltr; /* restore normal direction for content */

  font-size: 0.9rem;
  cursor: pointer;

  border: 1px solid var(--border-color);
  border-radius: 8px;

  padding: 20px 14px;
  background-color: var(--surface);
  color: var(--text-primary);

  display: flex;
  align-items: center;

}

/* Hover state */
.file-item:hover {
  background-color: var(--surface-hover);
}

/* Active selection */
.file-item.active {
  background: var(--primary);
  color: white;
  border-color: transparent;
  transform: translateY(-1px);
}



/* Main Content */
.content-area {
  flex-grow: 1;
  background-color: var(--surface);
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.code-diff-wrapper {
  height: 100%;
  overflow: auto;
  border-left: 1px solid var(--border-color);
  background-color: var(--surface-hover);
  transition: background-color 0.3s ease;
}

/* Loading Spinner */
.process-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
.lds-roller {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}
.lds-roller div {
  animation: lds-roller 1.2s linear infinite;
  transform-origin: 40px 40px;
}
.lds-roller div:after {
  content: " ";
  display: block;
  position: absolute;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--primary);
  margin: -4px 0 0 -4px;
}
@keyframes lds-roller {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Toggle Button */
.toggle-btn {
  border: none;
  background: transparent;
  color: var(--text-secondary);
  transition: color 0.2s;
  height: fit-content;
}
.toggle-btn:hover {
  color: var(--primary);
}
</style>
